import { Component, OnInit, ViewChild, Input, AfterViewInit, OnDestroy, Renderer2 } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AlertService, MessageSeverity } from '../../services/alert.service';
import { PaymentRequestService } from '../../services/payment-request.service';
import { PaymentRequestDto } from '../../models/payment-request.model';
import { DataTableDirective } from 'angular-datatables';
import { ConfigurationService } from '../../services/configuration.service';
interface PaymentRecord extends PaymentRequestDto {
  debitAmount?: number;
  otherPayment?: number;
  intoSecurityPayment?: number;
  sterlingPayment?: number;
  comments?: string;
  isEditing?: boolean;
  _saveTimer?: any;
}

@Component({
  selector: 'app-scpayments',
  templateUrl: './scpayments.component.html',
  styleUrls: ['./scpayments.component.scss'], standalone: false
})


export class ScpaymentsComponent implements OnInit, OnDestroy {

  @ViewChild(DataTableDirective, { static: false })
  datatableElement: DataTableDirective;

  dtOptions: any = {};
  dtTrigger: Subject<any> = new Subject<any>();

  isEditable = false;

  editableStartHour = 0;
  editableStartMinute = 1;
  editableEndHour = 15;
  editableEndMinute = 0;

  todayDate: string = '';
  constructor(
    private paymentService: PaymentRequestService,
    private alertService: AlertService,
    private configurations: ConfigurationService
  ) { }

  setTodayDate() {
    const today = new Date();

    const options: Intl.DateTimeFormatOptions = {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    };

    this.todayDate = today.toLocaleDateString('en-US', options);
  }

  ngOnInit(): void {

    this.setTodayDate();
    this.checkEditableTime();

    this.dtOptions = {
      pagingType: 'full_numbers',
      pageLength: this.configurations.defaultPageSize,
      lengthMenu: [10, 25, 50, 100],
      processing: true,
      responsive: true,
      searching: true,
      ordering: true,

      scrollY: '400px',
      scrollCollapse: true,
      fixedHeader: true,

      // ✅ Excel export
      dom: 'lBfrtip',
      buttons: [
        {
          extend: 'excel',
          text: 'Export to Excel',
          className: 'btn btn-success btn-sm',

          exportOptions: {
            columns: ':visible, :hidden',
            format: {
              body: function (data, row, column, node) {

                const $node = $(node);

                const input = $node.find('input');
                if (input.length) return input.val();

                return $node.text().trim();
              }
            }
          }
        }
      ],

      ajax: (params: any, callback) => {
        this.paymentService.getAllPaymentRequests().subscribe((resp: any) => {
          const data = resp?.data || resp || [];

          callback({ data });

          setTimeout(() => this.bindEditableEvents(), 100);
        });
      },

      columns: [

        // a. NetSuite Internal ID (read-only)
        { title: 'NetSuite Internal ID', data: 'netsuiteInternalID', defaultContent: '' },

        // b. ICD Company Name (read-only)
        { title: 'ICD Company Name', data: 'distributor', defaultContent: '' },

        // c. ICD Owner Name (read-only)
        { title: 'ICD Owner Name', data: 'owner', defaultContent: '' },

        // d. Parent Distributor Code (read-only)
        { title: 'Parent Distributor Code', data: 'distributorCode', defaultContent: '' },

        // e. Debit Amount (editable)
        {
          title: 'Debit Amount',
          data: 'debitAmount',
          render: (data, type, full) => {
            const value = data || 0;

            if (type === 'display') {
              if (!this.isEditable) return value;

              return `<input type="number" class="form-control form-control-sm editable"
              value="${value}" data-id="${full.id}" data-field="debitAmount" />`;
            }

            return value;
          }
        },

        // f. Sterling Payment (editable)
        {
          title: 'Sterling Payment',
          data: 'steringPayment',
          render: (data, type, full) => {
            const value = data || 0;

            if (type === 'display') {
              if (!this.isEditable) return value;

              return `<input type="number" class="form-control form-control-sm editable"
              value="${value}" data-id="${full.id}" data-field="steringPayment" />`;
            }

            return value;
          }
        },

        // g. Into Security (editable)
        {
          title: 'Into Security',
          data: 'paymentintoSecurity',
          render: (data, type, full) => {
            const value = data || 0;

            if (type === 'display') {
              if (!this.isEditable) return value;

              return `<input type="number" class="form-control form-control-sm editable"
              value="${value}" data-id="${full.id}" data-field="paymentintoSecurity" />`;
            }

            return value;
          }
        },

        // h. Other (editable)
        {
          title: 'Other',
          data: 'otherPayment',
          render: (data, type, full) => {
            const value = data || 0;

            if (type === 'display') {
              if (!this.isEditable) return value;

              return `<input type="number" class="form-control form-control-sm editable"
              value="${value}" data-id="${full.id}" data-field="otherPayment" />`;
            }

            return value;
          }
        },

        // i. Total (computed: Sterling + Into Security + Other)
        {
          title: 'Total',
          data: null,
          render: (data, type, full) => {
            return (parseFloat(full.steringPayment) || 0) +
              (parseFloat(full.paymentintoSecurity) || 0) +
              (parseFloat(full.otherPayment) || 0);
          }
        },

        // j. Comments (editable — required if Other > 0)
        {
          title: 'Comments',
          data: 'comments',
          render: (data, type, full) => {
            const value = data || '';
            const otherVal = parseFloat(full.otherPayment) || 0;
            const requiredClass = otherVal > 0 && !value.trim() ? 'comments-required' : '';

            if (type === 'display') {
              if (!this.isEditable) return value;

              return `<input type="text" class="form-control form-control-sm editable ${requiredClass}"
              value="${value}" data-id="${full.id}" data-field="comments"
              ${otherVal > 0 ? 'placeholder="Required when Other > 0"' : ''} />`;
            }

            return value;
          }
        },

        // k. Current Sec Bal. (read-only)
        { title: 'Current Sec Bal.', data: 'secBalance', defaultContent: '' },

        // l. Bank Mask (read-only, masked)
        {
          title: 'Bank Mask',
          data: 'bankAccountNo',
          defaultContent: '',
          render: (data, type, full) => {
            if (!data) return '';
            const str = data.toString();
            if (str.length <= 4) return '****' + str;
            return '****' + str.slice(-4);
          }
        }
      ]
    };
  }


  bindEditableEvents() {

    const tableBody = $('#tbldata tbody');

    tableBody.off('focus change', '.editable');

    // ✅ store original value on focus
    tableBody.on('focus', '.editable', function () {
      this._originalValue = this.value;
    });

    // ✅ trigger only on change
    tableBody.on('change', '.editable', (event: any) => {

      const input = event.target;

      const id = input.getAttribute('data-id');
      const field = input.getAttribute('data-field');

      let newValue = input.value;
      let oldValue = input._originalValue;

      // 🚫 prevent negative (except for comments)
      if (field !== 'comments' && newValue < 0) {
        newValue = 0;
        input.value = 0;
      }

      // ✅ ONLY call API if value changed
      if (field !== 'comments' && parseFloat(newValue) === parseFloat(oldValue)) {
        return;
      }
      if (field === 'comments' && newValue === oldValue) {
        return;
      }

      this.updateField(id, field, newValue);
    });
  }


  updateField(id: string, field: string, value: any) {

    if (field === 'comments') {
      value = (value || '').toString().trim();
    } else {
      value = Math.max(0, parseFloat(value) || 0);
    }

    const table = $('#tbldata').DataTable();

    const rowIndexes = table.rows().indexes();

    let foundIndex: any = null;

    rowIndexes.each((idx: any) => {
      const data = table.row(idx).data();

      const rowId = data.id || data.ID;

      if (String(rowId) === String(id)) {
        foundIndex = idx;
      }
    });

    if (foundIndex === null) {
      console.warn('Row not found for id:', id);
      return;
    }

    const rowData = table.row(foundIndex).data();

    rowData[field] = value;

    // Recompute Total = Sterling + Into Security + Other
    rowData.totalPayment =
      (parseFloat(rowData.steringPayment) || 0) +
      (parseFloat(rowData.paymentintoSecurity) || 0) +
      (parseFloat(rowData.otherPayment) || 0);

    table.row(foundIndex).data(rowData).draw(false);

    // Validate: if Other > 0 and Comments is empty, show warning
    const otherVal = parseFloat(rowData.otherPayment) || 0;
    if (field === 'otherPayment' && otherVal > 0 && !(rowData.comments || '').trim()) {
      this.alertService.showMessage('Comments Required', 'Comments are required when Other amount is greater than 0.', MessageSeverity.warn);
    }

    const payload = {
      id: id,

      debitAmount: rowData.debitAmount,
      paymentintoSecurity: rowData.paymentintoSecurity,
      steringPayment: rowData.steringPayment,
      otherPayment: rowData.otherPayment,
      comments: rowData.comments,
      totalPayment: rowData.totalPayment
    };


    this.paymentService.updatePaymentRequest(id, payload).subscribe({
      error: (err) => {
        console.error('API ERROR:', err);
      }
    });
  }

  checkEditableTime(): void {
    const est = new Date(
      new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })
    );

    const minutes = est.getHours() * 60 + est.getMinutes();
    const start = this.editableStartHour * 60 + this.editableStartMinute;
    const end = this.editableEndHour * 60;

    this.isEditable =  minutes >= start && minutes < end;
  }

  ngOnDestroy(): void {
    this.dtTrigger.unsubscribe();
  }
}
